import React, { useEffect, useMemo, useState } from "react"
import { Col, Container, NavLink, Row, NavItem, Nav, Button } from "reactstrap"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Spinners from "components/Shared/Spinners"

import {
  toggleActiveTab,
  setPayload,
  setBatchesPayload,
  setSignedAndUnSignedPayload,
  resetPayload,
  toggleFilterVisibility,
} from "store/reducers/contractReducer"
import { usePermissions } from "hooks"
import Datatable from "./component/Datatable"
import UploadNationalIdModal from "./component/UploadNationalIdModal"
import {
  getJobTitlesList,
  getStationShiftDropdownList,
} from "store/actions/commonAction"

import ExportsDatatable from "./component/ExportsDataTable"
import CandidatesDataTable from "./component/CandidatesDataTable"
import {
  getContractBatchesList,
  getNominatedCandidatesForContracts,
  getSignedAndUnSignedCandidates,
} from "store/actions/contractsAction"
import { Filter, FilterX } from "lucide-react"
import Filters from "components/Shared/Filters"
import { formatDateForAPI } from "components/Common/Utils"

const navItems = [
  {
    name: "Generation Requests",
    id: "exports",
  },
  {
    name: "Eligibal Candidates",
    id: "candidates",
  },
  {
    name: "Signed",
    id: "signed",
  },
  {
    name: "Not Signed",
    id: "unsigned",
  },
  {
    name: "Rejected",
    id: "rejected",
  },
]

const Contracts = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()
  const {
    loading,
    totalPages,
    totalCount,
    currentPage,
    payload,
    activeTab,
    candidates,
    nominatedCandidates,
    batches,
    signedAndUnSignedPayload,
    batchesPayload,
    isShowFilter,
  } = useSelector(state => state.Contract)

  const { jobTitlesList } = useSelector(state => state.Common)

  const { permissions, showAll } = usePermissions("contract")

  const [trigger, setTrigger] = useState(false)
  const [addEditModal, setAddEditModal] = useState(false)

  const formattedJobTitles = useMemo(() => {
    if (!jobTitlesList) return []

    return jobTitlesList.map(item => ({
      value: item.id,
      label: language === "en" ? item.name : item.name_ar,
    }))
  }, [jobTitlesList, language])

  useEffect(() => {
    getAllData()
  }, [trigger, activeTab])

  useEffect(() => {
    dispatch(getStationShiftDropdownList())
  }, [])

  useEffect(() => {
    dispatch(getJobTitlesList())
  }, [])

  const getAllData = () => {
    if (activeTab === "candidates") {
      const obj = {
        ...payload,
        filters: {
          ...payload.filters,
          is_saudi:
            payload.filters.is_saudi === "all"
              ? null
              : payload.filters.is_saudi === "saudi"
              ? 1
              : 0,
        },
      }
      dispatch(getNominatedCandidatesForContracts(obj))
    } else if (
      activeTab === "signed" ||
      activeTab === "unsigned" ||
      activeTab === "rejected"
    ) {
      let obj = {
        ...signedAndUnSignedPayload,
        filters: {
          ...signedAndUnSignedPayload.filters,
          type: activeTab === "signed" ? 2 : activeTab === "unsigned" ? 1 : 3,
          date_range:
            signedAndUnSignedPayload.filters?.date_range?.length > 1
              ? {
                  start_date: formatDateForAPI(
                    signedAndUnSignedPayload.filters.date_range[0]
                  ),
                  end_date: formatDateForAPI(
                    signedAndUnSignedPayload.filters.date_range[1]
                  ),
                }
              : {},
          is_saudi:
            signedAndUnSignedPayload.filters.is_saudi === "all"
              ? null
              : signedAndUnSignedPayload.filters.is_saudi === "saudi"
              ? 1
              : 0,
        },
      }
      dispatch(getSignedAndUnSignedCandidates(obj))
    } else {
      const obj = {
        ...batchesPayload,
        filters: {
          ...batchesPayload.filters,
          date_range:
            batchesPayload.filters?.date_range?.length > 1
              ? {
                  start_date: formatDateForAPI(
                    batchesPayload.filters.date_range[0]
                  ),
                  end_date: formatDateForAPI(
                    batchesPayload.filters.date_range[1]
                  ),
                }
              : {},
        },
      }
      dispatch(getContractBatchesList(obj))
    }
  }

  const onPageChange = page => {
    if (activeTab === "exports")
      dispatch(setBatchesPayload({ page_number: page }))
    else if (activeTab === "candidates")
      dispatch(setPayload({ page_number: page }))
    else dispatch(setSignedAndUnSignedPayload({ page_number: page }))
    setTrigger(!trigger)
  }

  const filters = useMemo(() => {
    if (activeTab === "exports") {
      return {
        keyword: {
          type: "text",
          placeholder: t("Search by name"),
          label: t("Search By"),
          colSize: 4,
        },
        date_range: {
          type: "dateRange",
          label: t("Select Date"),
          colSize: 4,
        },
      }
    } else if (activeTab === "candidates") {
      return {
        keyword: {
          type: "text",
          placeholder: t("Search by name,email,national id or phone number"),
          label: t("Search By"),
          colSize: 4,
        },
        is_saudi: {
          type: "select",
          placeholder: t("Select Nationality"),
          label: t("Select Nationality"),
          options: [
            { value: "saudi", label: t("Saudi") },
            { value: "non_saudi", label: t("Non Saudi") },
          ],
          colSize: 4,
        },
        job_title_ids: {
          type: "select",
          placeholder: t("Select Job Title"),
          label: t("Select Job Title"),
          options: formattedJobTitles,
          colSize: 4,
          multiple: true,
        },
      }
    } else {
      return {
        keyword: {
          type: "text",
          placeholder: t("Search by name,email,national id or phone number"),
          label: t("Search By"),
          colSize: 4,
        },
        is_saudi: {
          type: "select",
          placeholder: t("Select Nationality"),
          label: t("Select Nationality"),
          options: [
            { value: "saudi", label: t("Saudi") },
            { value: "non_saudi", label: t("Non Saudi") },
          ],
          colSize: 4,
        },
        job_title_ids: {
          type: "select",
          placeholder: t("Select Job Title"),
          label: t("Select Job Title"),
          options: formattedJobTitles,
          colSize: 4,
          multiple: true,
        },
        date_range: {
          type: "dateRange",
          label: t("Select Date"),
          colSize: 4,
        },
      }
    }
  }, [activeTab])

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  if (loading) return <Spinners />

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <div className="d-flex mb-4 justify-content-between align-items-center">
                <h4 className="card-title">{t("Contracts")}</h4>
                <div className="d-flex gap-3 align-items-center justify-content-center">
                  {(showAll || permissions.includes("can_generate_batch")) && (
                    <div>
                      <button
                        className="main_btn"
                        onClick={() => {
                          setAddEditModal(true)
                        }}
                      >
                        {t("Generate Contracts")}
                      </button>
                    </div>
                  )}
                  <Button
                    color="primary"
                    onClick={() => dispatch(toggleFilterVisibility())}
                    title={t("Show/Hide Filter")}
                  >
                    {isShowFilter ? <FilterX /> : <Filter />}
                  </Button>
                </div>
              </div>
            </Col>
            <Col xs="12">
              <Nav tabs className="one_line_parent mb-3">
                {navItems.map(nav => (
                  <NavItem key={nav.id}>
                    <NavLink
                      className={activeTab === nav.id ? "active" : ""}
                      onClick={() => dispatch(toggleActiveTab(nav.id))}
                    >
                      {t(nav.name)}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </Col>
            {isShowFilter && (
              <Col xs="12">
                <Filters
                  filters={filters}
                  state={
                    activeTab === "candidates"
                      ? payload.filters
                      : activeTab === "exports"
                      ? batchesPayload.filters
                      : signedAndUnSignedPayload.filters
                  }
                  setState={data => {
                    if (activeTab === "exports") {
                      dispatch(
                        setBatchesPayload({
                          filters: { ...batchesPayload.filters, ...data },
                        })
                      )
                    } else if (
                      activeTab === "signed" ||
                      activeTab === "unsigned"
                    ) {
                      dispatch(
                        setSignedAndUnSignedPayload({
                          filters: {
                            ...signedAndUnSignedPayload.filters,
                            ...data,
                          },
                        })
                      )
                    } else {
                      dispatch(
                        setPayload({
                          filters: { ...payload.filters, ...data },
                        })
                      )
                    }
                  }}
                  handleSearch={() => setTrigger(!trigger)}
                  handleReset={handleReset}
                />
              </Col>
            )}
            <Col xs="12">
              {activeTab === "exports" ? (
                <ExportsDatatable
                  data={batches}
                  currentPage={currentPage}
                  loading={loading}
                  onPageChange={onPageChange}
                  totalCount={totalCount}
                  totalPages={totalPages}
                  trigger={() => setTrigger(!trigger)}
                  payload={payload}
                  permissions={permissions}
                  showAll={showAll}
                />
              ) : activeTab === "signed" ||
                activeTab === "unsigned" ||
                activeTab === "rejected" ? (
                <CandidatesDataTable
                  data={candidates}
                  currentPage={currentPage}
                  loading={loading}
                  onPageChange={onPageChange}
                  totalCount={totalCount}
                  totalPages={totalPages}
                  activeTab={activeTab}
                  trigger={() => setTrigger(!trigger)}
                  payload={payload}
                  showAll={showAll}
                  permissions={permissions}
                />
              ) : (
                <Datatable
                  data={nominatedCandidates}
                  currentPage={currentPage}
                  loading={loading}
                  onPageChange={onPageChange}
                  totalCount={totalCount}
                  totalPages={totalPages}
                  trigger={() => setTrigger(!trigger)}
                  payload={payload}
                  permissions={permissions}
                  showAll={showAll}
                />
              )}
            </Col>
          </Row>
        </Container>
      </div>
      {addEditModal && (
        <UploadNationalIdModal
          isOpen={addEditModal}
          toggle={() => setAddEditModal(false)}
          trigger={() => setTrigger(!trigger)}
        />
      )}
    </>
  )
}

export default Contracts
